// Генерация пар на тур (стадию) по круговой системе.
//
// Правила:
// - пара (A, B) в пределах круга встречается один раз, кто дома — неважно;
// - круг заканчивается, когда сыграны все пары; только после этого пары
//   становятся доступны снова (второй круг), и об этом надо предупредить;
// - в туре каждая команда играет не больше одного раза; матчи, уже созданные
//   в этом туре, учитываются — их команды заняты;
// - в групповой лиге всё считается внутри каждой группы отдельно;
// - при нечётном числе команд одна команда в туре отдыхает, и за круг
//   каждая отдыхает один раз;
// - пары тура подбираются так, чтобы оставшиеся туры круга можно было
//   составить без повторов (иначе на последних турах генератор упрётся в тупик).
//
// Модуль без React и без запросов — его можно проверить тестами.

const BYE = '__bye__';
const BUDGET_EXCEEDED = 'budget';

export const pairKey = (a, b) => {
    const x = String(a);
    const y = String(b);
    return x < y ? `${x}|${y}` : `${y}|${x}`;
};

const splitKey = (key) => key.split('|');

export const stageIdOf = (match) => (match.stage_id !== undefined && match.stage_id !== null
    ? match.stage_id : match.tour_id);

// ---------------------------------------------------------------------------
// История: сколько раз встречались пары, кто отдыхал, кто занят в текущем туре
// ---------------------------------------------------------------------------

export function buildHistory(teamIds, matches, {
    currentStageId,
    isCountedStage = () => true,
    stageOrder = (stageId) => Number(stageId) || 0,
    busyTeams = new Set(),
} = {}) {
    const teamSet = new Set(teamIds.map(String));
    const pairCounts = new Map();
    const byStage = new Map();
    const lastMeeting = new Map();
    const lastSide = new Map();
    const homeCount = new Map();
    const awayCount = new Map();
    const current = String(currentStageId);

    (matches || []).forEach((match, index) => {
        if (match.home_team_id == null || match.guest_team_id == null) return;
        const home = String(match.home_team_id);
        const guest = String(match.guest_team_id);
        if (home === guest || !teamSet.has(home) || !teamSet.has(guest)) return;
        const stageId = stageIdOf(match);
        if (!isCountedStage(stageId)) return;

        const key = pairKey(home, guest);
        pairCounts.set(key, (pairCounts.get(key) || 0) + 1);

        const stageKey = String(stageId);
        if (!byStage.has(stageKey)) byStage.set(stageKey, new Set());
        byStage.get(stageKey).add(home).add(guest);

        // Порядок: сначала по стадии, внутри — по id матча
        const order = stageOrder(stageId) * 1e9 + (Number(match.id) || index);
        const prevMeeting = lastMeeting.get(key);
        if (!prevMeeting || prevMeeting.order < order) lastMeeting.set(key, { home, order });
        [[home, 'home'], [guest, 'away']].forEach(([team, side]) => {
            const prev = lastSide.get(team);
            if (!prev || prev.order < order) lastSide.set(team, { side, order });
        });
        homeCount.set(home, (homeCount.get(home) || 0) + 1);
        awayCount.set(guest, (awayCount.get(guest) || 0) + 1);
    });

    // Отдых: в туре, где группа играла, команда без матча отдыхала
    const byeCounts = new Map();
    byStage.forEach((playing, stageKey) => {
        if (stageKey === current) return;
        teamSet.forEach(team => {
            if (!playing.has(team)) byeCounts.set(team, (byeCounts.get(team) || 0) + 1);
        });
    });

    const busy = new Set(byStage.get(current) || []);
    busyTeams.forEach(team => { if (teamSet.has(String(team))) busy.add(String(team)); });

    return { pairCounts, byeCounts, busy, lastMeeting, lastSide, homeCount, awayCount };
}

// ---------------------------------------------------------------------------
// Таблица Бергера — первые кандидаты: если прошлые туры шли по ней же,
// она гарантированно доводит круг до конца
// ---------------------------------------------------------------------------

const bergerRounds = (vertices) => {
    const n = vertices.length;
    if (n < 2) return [];
    let arr = [...vertices];
    const rounds = [];
    for (let r = 0; r < n - 1; r++) {
        const round = [];
        for (let i = 0; i < n / 2; i++) round.push(pairKey(arr[i], arr[n - 1 - i]));
        rounds.push(round);
        arr = [arr[0], arr[n - 1], ...arr.slice(1, n - 1)];
    }
    return rounds;
};

const buildAdjacency = (vertices, edges) => {
    const adj = new Map(vertices.map(v => [v, []]));
    edges.forEach(key => {
        const [a, b] = splitKey(key);
        if (adj.has(a) && adj.has(b)) {
            adj.get(a).push(b);
            adj.get(b).push(a);
        }
    });
    return adj;
};

// Можно ли разбить оставшиеся пары круга на полные туры.
// true / false; при исчерпании бюджета — null (неизвестно)
const canFinishCircle = (edges, vertices, berger, budget) => {
    if (edges.size === 0) return true;

    // Все команды должны иметь одинаковое число оставшихся соперников,
    // иначе история нерегулярная (ручные туры) — проверить нечем
    const degree = new Map(vertices.map(v => [v, 0]));
    edges.forEach(key => splitKey(key).forEach(v => degree.set(v, (degree.get(v) || 0) + 1)));
    const degrees = new Set(degree.values());
    if (degrees.size !== 1) return null;

    // Быстрый путь: остаток — ровно оставшиеся туры таблицы Бергера
    const covering = berger.filter(round => round.every(key => edges.has(key)));
    const covered = new Set();
    covering.forEach(round => round.forEach(key => covered.add(key)));
    if (covered.size === edges.size) return true;

    const factorize = (rest) => {
        if (rest.size === 0) return true;
        const adj = buildAdjacency(vertices, rest);
        const matched = new Set();
        const chosen = [];
        const search = () => {
            budget.left -= 1;
            if (budget.left < 0) throw BUDGET_EXCEEDED;
            if (matched.size === vertices.length) {
                const next = new Set(rest);
                chosen.forEach(key => next.delete(key));
                return factorize(next);
            }
            let pick = null;
            let options = null;
            for (const v of vertices) {
                if (matched.has(v)) continue;
                const free = adj.get(v).filter(u => !matched.has(u));
                if (free.length === 0) return false;
                if (!options || free.length < options.length) {
                    pick = v;
                    options = free;
                }
            }
            for (const u of options) {
                matched.add(pick).add(u);
                chosen.push(pairKey(pick, u));
                if (search()) return true;
                chosen.pop();
                matched.delete(pick);
                matched.delete(u);
            }
            return false;
        };
        return search();
    };

    try {
        return factorize(edges);
    } catch (error) {
        if (error === BUDGET_EXCEEDED) return null;
        throw error;
    }
};

// Все полные паросочетания команд тура (с ограничением по числу и бюджету)
const enumerateMatchings = (roundVertices, allowed, preferred, limit, budget) => {
    const result = [];
    const seen = new Set();
    const push = (keys) => {
        const id = [...keys].sort().join(',');
        if (seen.has(id)) return;
        seen.add(id);
        result.push(keys);
    };

    // Сначала туры Бергера, которые целиком доступны
    const roundSet = new Set(roundVertices);
    preferred.forEach(round => {
        if (result.length >= limit) return;
        const keys = round.filter(key => splitKey(key).every(v => roundSet.has(v)));
        const covered = new Set();
        keys.forEach(key => splitKey(key).forEach(v => covered.add(v)));
        if (covered.size === roundVertices.length && keys.every(key => allowed.has(key))) push(keys);
    });

    const adj = buildAdjacency(roundVertices, [...allowed].filter(key => splitKey(key).every(v => roundSet.has(v))));
    const matched = new Set();
    const chosen = [];
    const search = () => {
        if (result.length >= limit) return;
        budget.left -= 1;
        if (budget.left < 0) return;
        if (matched.size === roundVertices.length) {
            push([...chosen]);
            return;
        }
        let pick = null;
        let options = null;
        for (const v of roundVertices) {
            if (matched.has(v)) continue;
            const free = adj.get(v).filter(u => !matched.has(u));
            if (free.length === 0) return;
            if (!options || free.length < options.length) {
                pick = v;
                options = free;
            }
        }
        for (const u of options) {
            matched.add(pick).add(u);
            chosen.push(pairKey(pick, u));
            search();
            chosen.pop();
            matched.delete(pick);
            matched.delete(u);
            if (result.length >= limit || budget.left < 0) return;
        }
    };
    search();
    return result;
};

// Наибольшее паросочетание — когда полный тур собрать нельзя
const maximumMatching = (roundVertices, allowed, budget) => {
    const roundSet = new Set(roundVertices);
    const adj = buildAdjacency(roundVertices, [...allowed].filter(key => splitKey(key).every(v => roundSet.has(v))));
    let best = [];
    const matched = new Set();
    const chosen = [];
    const search = (index) => {
        budget.left -= 1;
        if (budget.left < 0) return;
        if (chosen.length > best.length) best = [...chosen];
        if (index >= roundVertices.length) return;
        const v = roundVertices[index];
        if (matched.has(v)) {
            search(index + 1);
            return;
        }
        // Верхняя оценка: даже если все оставшиеся найдут пару, лучше не станет
        const unmatchedLeft = roundVertices.slice(index).filter(u => !matched.has(u)).length;
        if (chosen.length + Math.floor(unmatchedLeft / 2) <= best.length) return;
        adj.get(v).filter(u => !matched.has(u)).forEach(u => {
            matched.add(v).add(u);
            chosen.push(pairKey(v, u));
            search(index + 1);
            chosen.pop();
            matched.delete(v);
            matched.delete(u);
        });
        search(index + 1); // v без пары
    };
    search(0);
    return best;
};

// ---------------------------------------------------------------------------
// Один тур одной группы
// ---------------------------------------------------------------------------

export function planRound({ teamIds, history, variant = 0, maxVariants = 12, budget = 400000 }) {
    const ids = teamIds.map(String);
    const n = ids.length;
    const empty = { pairs: [], perfect: true, unmatched: [], restingTeam: null, circle: 1, newCircle: false, variants: 0, variantIndex: 0, scheduleRisk: false };
    if (n < 2) return empty;

    const odd = n % 2 === 1;
    const vertices = odd ? [...ids, BYE] : ids;
    const count = (a, b) => history.pairCounts.get(pairKey(a, b)) || 0;

    let level = Infinity;
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) level = Math.min(level, count(ids[i], ids[j]));
    }
    let circleStarted = false;
    for (let i = 0; i < n && !circleStarted; i++) {
        for (let j = i + 1; j < n; j++) {
            if (count(ids[i], ids[j]) > level) {
                circleStarted = true;
                break;
            }
        }
    }

    const buildAllowed = (strictRest) => {
        const allowed = new Set();
        for (let i = 0; i < n; i++) {
            for (let j = i + 1; j < n; j++) {
                if (count(ids[i], ids[j]) === level) allowed.add(pairKey(ids[i], ids[j]));
            }
        }
        if (odd) {
            ids.forEach(team => {
                if (!strictRest || (history.byeCounts.get(team) || 0) <= level) allowed.add(pairKey(team, BYE));
            });
        }
        return allowed;
    };

    const free = ids.filter(team => !history.busy.has(team));
    const roundVertices = free.length % 2 === 1 && odd ? [...free, BYE] : free;
    const berger = bergerRounds(vertices);
    const counter = { left: budget };

    const solve = (strictRest) => {
        const allowed = buildAllowed(strictRest);
        if (roundVertices.length % 2 === 1) return { allowed, solutions: [], risky: [] };
        const matchings = enumerateMatchings(roundVertices, allowed, berger, maxVariants * 4, counter);
        const solutions = [];
        const risky = [];
        matchings.forEach(keys => {
            if (solutions.length >= maxVariants) return;
            const rest = new Set(allowed);
            keys.forEach(key => rest.delete(key));
            const finish = canFinishCircle(rest, vertices, berger, counter);
            if (finish === false) risky.push(keys);
            else solutions.push(keys);
        });
        return { allowed, solutions, risky };
    };

    let attempt = solve(true);
    // Если отдыхать по правилам некому (ручная история) — ослабляем правило отдыха
    if (odd && attempt.solutions.length === 0 && attempt.risky.length === 0) attempt = solve(false);

    const toResult = (keys, extra) => {
        const pairs = [];
        let restingTeam = null;
        keys.forEach(key => {
            const [a, b] = splitKey(key);
            if (a === BYE) restingTeam = b;
            else if (b === BYE) restingTeam = a;
            else pairs.push([a, b]);
        });
        return {
            ...empty,
            pairs,
            restingTeam,
            circle: level + 1,
            newCircle: level > 0 && !circleStarted,
            ...extra,
        };
    };

    if (attempt.solutions.length > 0) {
        const index = ((variant % attempt.solutions.length) + attempt.solutions.length) % attempt.solutions.length;
        return toResult(attempt.solutions[index], { variants: attempt.solutions.length, variantIndex: index });
    }
    if (attempt.risky.length > 0) {
        // Полный тур есть, но после него круг без повторов не доиграть
        return toResult(attempt.risky[0], { variants: 1, variantIndex: 0, scheduleRisk: true });
    }

    // Полный тур не собирается: берём максимум возможных матчей
    const best = maximumMatching(roundVertices, attempt.allowed, { left: budget });
    const covered = new Set();
    best.forEach(key => splitKey(key).forEach(v => covered.add(v)));
    const result = toResult(best, { perfect: false, variants: best.length ? 1 : 0 });
    result.unmatched = free.filter(team => !covered.has(team) && team !== result.restingTeam);
    // При нечётной группе один свободный без пары — это просто отдыхающий
    if (odd && !result.restingTeam && result.unmatched.length === 1) {
        result.restingTeam = result.unmatched[0];
        result.unmatched = [];
    }
    return result;
}

// Кто дома: во втором круге — наоборот относительно прошлой встречи,
// иначе дома тот, кто прошлый матч играл в гостях
export function orientPair(a, b, history) {
    const met = history.lastMeeting.get(pairKey(a, b));
    if (met) return met.home === String(a) ? [String(b), String(a)] : [String(a), String(b)];
    const wantsHome = (team) => {
        const last = history.lastSide.get(String(team));
        return last ? last.side === 'away' : null;
    };
    const aw = wantsHome(a);
    const bw = wantsHome(b);
    if (aw === true && bw !== true) return [String(a), String(b)];
    if (bw === true && aw !== true) return [String(b), String(a)];
    if (aw === false && bw !== false) return [String(b), String(a)];
    if (bw === false && aw !== false) return [String(a), String(b)];
    const balance = (team) => (history.homeCount.get(String(team)) || 0) - (history.awayCount.get(String(team)) || 0);
    return balance(a) > balance(b) ? [String(b), String(a)] : [String(a), String(b)];
}

// ---------------------------------------------------------------------------
// Вся стадия: по группам (или одна «группа» для обычной лиги)
// ---------------------------------------------------------------------------

// teamIds — порядок команд (как отдаёт сервер); groupOf — id → имя группы,
// либо null для обычной лиги
export function planStage({
    teamIds,
    groupOf = null,
    matches,
    stageId,
    isCountedStage = () => true,
    stageOrder,
    variant = 0,
}) {
    const ids = [...new Set((teamIds || []).map(String))];
    const current = String(stageId);
    const stageMatches = (matches || []).filter(m => String(stageIdOf(m)) === current
        && m.home_team_id != null && m.guest_team_id != null);
    const busyAll = new Set();
    stageMatches.forEach(m => {
        busyAll.add(String(m.home_team_id));
        busyAll.add(String(m.guest_team_id));
    });

    const units = [];
    const skippedTeams = [];
    if (groupOf) {
        const byGroup = new Map();
        ids.forEach(team => {
            const group = groupOf[team];
            if (!group) {
                skippedTeams.push(team);
                return;
            }
            if (!byGroup.has(group)) byGroup.set(group, []);
            byGroup.get(group).push(team);
        });
        [...byGroup.keys()]
            .sort((a, b) => a.localeCompare(b, 'ru'))
            .forEach(group => units.push({ groupName: group, teamIds: byGroup.get(group) }));
    } else {
        units.push({ groupName: null, teamIds: ids });
    }

    const unitOf = new Map();
    units.forEach((unit, index) => unit.teamIds.forEach(team => unitOf.set(team, index)));

    // Матчи тура, которые не вписываются в группы: межгрупповые или с командой без группы
    const strayMatches = stageMatches.filter(m => {
        const h = unitOf.get(String(m.home_team_id));
        const g = unitOf.get(String(m.guest_team_id));
        return h === undefined || g === undefined || h !== g;
    });

    const results = units.map(unit => {
        const history = buildHistory(unit.teamIds, matches, {
            currentStageId: stageId,
            isCountedStage,
            stageOrder,
            busyTeams: busyAll,
        });
        const unitSet = new Set(unit.teamIds);
        const existing = stageMatches.filter(m => unitSet.has(String(m.home_team_id))
            && unitSet.has(String(m.guest_team_id))).length;
        const perTour = Math.floor(unit.teamIds.length / 2);
        const needed = perTour - existing;
        const base = { ...unit, perTour, existing, needed, history };
        if (needed <= 0 || unit.teamIds.length < 2) {
            return { ...base, full: true, round: null, pairs: [] };
        }
        const round = planRound({ teamIds: unit.teamIds, history, variant });
        const pairs = round.pairs.map(([a, b]) => {
            const [home, guest] = orientPair(a, b, history);
            return { home, guest, playedBefore: history.pairCounts.get(pairKey(a, b)) || 0 };
        });
        return { ...base, full: false, round, pairs };
    });

    return {
        units: results,
        skippedTeams,
        strayMatches,
        full: results.every(unit => unit.full),
        variants: Math.max(1, ...results.map(unit => (unit.round ? unit.round.variants : 1))),
    };
}

// Проверка перед сохранением по свежим данным: не появились ли за время
// превью матчи с этими командами в туре или эти пары в круге
export function findConflicts(planned, freshPlan) {
    const conflicts = [];
    planned.forEach(match => {
        const unit = freshPlan.units.find(u => u.teamIds.includes(String(match.home_team_id)));
        if (!unit) {
            conflicts.push(match);
            return;
        }
        const busy = unit.history.busy;
        const nowPlayed = unit.history.pairCounts.get(pairKey(match.home_team_id, match.guest_team_id)) || 0;
        if (busy.has(String(match.home_team_id)) || busy.has(String(match.guest_team_id))
            || nowPlayed > (match.played_before || 0)) {
            conflicts.push(match);
        }
    });
    return conflicts;
}
